import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Heart, Users, Building2, MapPin, ChevronRight, ArrowRight, 
  Download, ExternalLink, Mail, Phone, Facebook, Twitter, Linkedin,
  TrendingUp, Globe, Shield, Star
} from 'lucide-react';

export default function PublicHomePage() {
  const [stats, setStats] = useState(null);
  const [mapaData, setMapaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animatedStats, setAnimatedStats] = useState({
    instituicoes: 0,
    pessoas: 0,
    voluntarios: 0,
    distritos: 0
  });

  useEffect(() => {
    loadPublicData();
  }, []);

  // Animate stats on load
  useEffect(() => {
    if (stats) {
      const duration = 2000;
      const frames = 60;
      const interval = duration / frames;
      
      let frame = 0;
      const timer = setInterval(() => {
        frame++;
        const progress = frame / frames;
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        setAnimatedStats({
          instituicoes: Math.round(stats.instituicoes_ativas * easeOut),
          pessoas: Math.round(stats.total_utentes * easeOut),
          voluntarios: Math.round(stats.total_voluntarios * easeOut),
          distritos: Math.round(18 * easeOut)
        });
        
        if (frame >= frames) clearInterval(timer);
      }, interval);
      
      return () => clearInterval(timer);
    }
  }, [stats]);

  const loadPublicData = async () => {
    try {
      const [statsRes, mapaRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/mapa-instituicoes`)
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (mapaRes.ok) setMapaData(await mapaRes.json());
    } catch (error) {
      console.error('Error loading public data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString('pt-PT');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="public-home-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="w-8 h-8 text-[#0066CC]" fill="#0066CC" />
              <span className="text-xl font-bold text-slate-900">ENTRAJUDATA</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/publico" className="text-slate-600 hover:text-[#0066CC] transition-colors">Início</Link>
              <Link to="/publico/dados-abertos" className="text-slate-600 hover:text-[#0066CC] transition-colors">Dados Abertos</Link>
              <Link to="/publico/instituicoes" className="text-slate-600 hover:text-[#0066CC] transition-colors">Instituições</Link>
              <Link to="/publico/voluntariado" className="text-slate-600 hover:text-[#0066CC] transition-colors">Ser Voluntário</Link>
              <Link to="/quero-doar" className="text-rose-500 hover:text-rose-600 font-medium transition-colors">Quero Doar</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/portal/login">
                <Button variant="outline" size="sm">Portal Instituições</Button>
              </Link>
              <Link to="/login">
                <Button size="sm" className="bg-[#0066CC] hover:bg-[#0055AA]">Acesso EA</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#0066CC]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-[#0066CC]/10 text-[#0066CC] mb-4">
              Plataforma de Gestão Social
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-slate-900">
              Dados ao Serviço da
              <span className="text-[#0066CC]"> Solidariedade</span>
            </h1>
            <p className="mt-6 text-xl leading-relaxed text-slate-600">
              A plataforma que conecta mais de <strong>{formatNumber(animatedStats.instituicoes)}</strong> instituições 
              servindo <strong>{formatNumber(animatedStats.pessoas)}</strong> pessoas em Portugal.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/publico/dados-abertos">
                <Button size="lg" className="bg-[#0066CC] hover:bg-[#0055AA]">
                  Explorar Dados
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/quero-doar">
                <Button size="lg" className="bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white">
                  <Heart className="w-5 h-5 mr-2" fill="white" />
                  Quero Doar
                </Button>
              </Link>
              <Link to="/publico/voluntariado">
                <Button size="lg" variant="outline" className={''}>
                  <Users className="w-5 h-5 mr-2 text-pink-500" />
                  Ser Voluntário
                </Button>
              </Link>
            </div>
          </div>

          {/* Animated Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            <StatCard
              icon={Building2}
              value={formatNumber(animatedStats.instituicoes)}
              suffix="+"
              label="Instituições"
              color="blue"
              
            />
            <StatCard
              icon={Users}
              value={formatNumber(animatedStats.pessoas)}
              label="Pessoas Servidas"
              color="green"
              
            />
            <StatCard
              icon={Heart}
              value={formatNumber(animatedStats.voluntarios)}
              label="Voluntários"
              color="pink"
              
            />
            <StatCard
              icon={MapPin}
              value={animatedStats.distritos}
              label="Distritos"
              color="amber"
              
            />
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Impacto em Números</h2>
            <p className="mt-2 text-lg text-slate-600">Dados agregados da rede de instituições ENTRAJUDA</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <ImpactCard value={stats?.valencia_mais_comum || "Apoio Social"} label="Resposta Social Mais Comum" icon={Star}  />
            <ImpactCard value={stats?.pedidos_recursos || 0} label="Pedidos de Apoio" icon={TrendingUp} trend="+15%"  />
            <ImpactCard value={stats?.missoes_voluntariado || 0} label="Missões Voluntariado" icon={Heart}  />
            <ImpactCard value={stats?.documentos_geridos || 0} label="Documentos Geridos" icon={Shield}  />
            <ImpactCard value={(stats?.taxa_atualizacao || 0) + '%'} label="Taxa Atualização" icon={TrendingUp}  />
            <ImpactCard value={stats?.parceiros || 0} label="Parceiros" icon={Globe}  />
          </div>
        </div>
      </section>

      {/* Map Section - Simplified */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Onde Estamos</h2>
            <p className="mt-2 text-lg text-slate-600">Distribuição geográfica das instituições em Portugal</p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {mapaData.filter(d => d.count > 0).map((distrito) => (
              <div 
                key={distrito.distrito_id} 
                className="rounded-xl p-4 shadow-sm border hover:shadow-md hover:border-[#0066CC] transition-all cursor-pointer text-center bg-white border-slate-100"
              >
                <div 
                  className="w-12 h-12 mx-auto rounded-full bg-[#0066CC] flex items-center justify-center text-white font-bold text-lg mb-2"
                  style={{
                    width: Math.max(40, Math.min(56, 32 + distrito.count * 2)),
                    height: Math.max(40, Math.min(56, 32 + distrito.count * 2)),
                  }}
                >
                  {distrito.count}
                </div>
                <p className="font-medium text-slate-900 text-sm">{distrito.distrito}</p>
                <p className="text-xs text-slate-500">instituições</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0066CC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">Junte-se à nossa rede</h2>
          <p className="mt-4 text-lg text-blue-100">
            Seja uma instituição parceira ou voluntário, faça parte desta missão de solidariedade.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/portal/registar">
              <Button size="lg" variant="secondary">
                <Building2 className="w-5 h-5 mr-2" />
                Registar Instituição
              </Button>
            </Link>
            <Link to="/publico/voluntariado">
              <Button size="lg" className="bg-white text-[#0066CC] hover:bg-blue-50">
                <Heart className="w-5 h-5 mr-2" />
                Ser Voluntário
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Transparência</h2>
            <p className="mt-2 text-lg text-slate-600">Acesso aberto a dados e relatórios</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-all cursor-pointer group ">
              <CardContent className="pt-6">
                <Download className="w-10 h-10 text-[#0066CC] mb-4" />
                <h3 className="font-semibold text-lg mb-2 group-hover:text-[#0066CC] transition-colors ">Dados Abertos</h3>
                <p className="text-sm text-slate-600">Datasets em CSV/JSON para análise e investigação.</p>
                <Link to="/publico/dados-abertos" className="text-[#0066CC] text-sm mt-4 inline-flex items-center">
                  Aceder <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-all cursor-pointer group ">
              <CardContent className="pt-6">
                <ExternalLink className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2 group-hover:text-green-600 transition-colors ">Relatórios</h3>
                <p className="text-sm text-slate-600">Relatórios trimestrais e anuais de impacto.</p>
                <span className="text-slate-400 text-sm mt-4 inline-flex items-center">
                  Em breve <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-all cursor-pointer group ">
              <CardContent className="pt-6">
                <Globe className="w-10 h-10 text-purple-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2 group-hover:text-purple-600 transition-colors">API Pública</h3>
                <p className="text-slate-600 text-sm">Integre dados da ENTRAJUDATA na sua aplicação.</p>
                <span className="text-slate-400 text-sm mt-4 inline-flex items-center">
                  Em breve <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-8 h-8 text-[#0066CC]" fill="#0066CC" />
                <span className="text-xl font-bold">ENTRAJUDATA</span>
              </div>
              <p className="text-slate-400 text-sm">
                Plataforma de gestão de dados de instituições de solidariedade social em Portugal.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Links Rápidos</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/publico" className="hover:text-white transition-colors">Início</Link></li>
                <li><Link to="/publico/dados-abertos" className="hover:text-white transition-colors">Dados Abertos</Link></li>
                <li><Link to="/publico/instituicoes" className="hover:text-white transition-colors">Instituições</Link></li>
                <li><Link to="/publico/voluntariado" className="hover:text-white transition-colors">Voluntariado</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@entrajuda.pt</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>213 600 500</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Redes Sociais</h4>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#0066CC] transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#0066CC] transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-[#0066CC] transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.</p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-white transition-colors">Termos de Uso</a>
              <a href="#" className="hover:text-white transition-colors">Política de Privacidade</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ icon: Icon, value, suffix = '', label, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-[#0066CC]',
    green: 'bg-green-50 text-green-600',
    pink: 'bg-pink-50 text-pink-600',
    amber: 'bg-amber-50 text-amber-600'
  };

  return (
    <div className="rounded-xl p-6 shadow-sm border hover:shadow-lg transition-all bg-white border-slate-100">
      <div className="w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-3xl font-bold text-slate-900">
        {value}{suffix}
      </p>
      <p className={'text-slate-600'}>{label}</p>
    </div>
  );
}

function ImpactCard({ value, label, icon: Icon, trend }) {
  return (
    <Card className="text-center hover:shadow-lg transition-all bg-white">
      <CardContent className="pt-6">
        <Icon className="w-8 h-8 mx-auto mb-3 text-slate-400" />
        <p className="text-2xl font-bold text-slate-900">
          {typeof value === 'number' ? value.toLocaleString('pt-PT') : value}
        </p>
        <p className="text-sm text-slate-600">{label}</p>
        {trend && (
          <Badge className="mt-2 bg-green-100 text-green-700">{trend}</Badge>
        )}
      </CardContent>
    </Card>
  );
}
